<template>
  <section class="expandable-card" :class="{ 'is-mobile': isMobile }">
    <div v-if="isMobile" class="mobile-layout">

      <!-- TITLE -->
      <h2 class="ec-title-full" :title="content.title" :class="contentPositionClass">
        {{ truncatedTitle }}
      </h2>

      <!-- IMAGE -->
      <div class="ec-image">
        <img :src="content.cardImageUrl" :alt="content.cardImageAlt || ''" />
      </div>

      <!-- SUBTITLE -->
      <h3 v-if="content.subTitle" class="ec-subtitle" :class="contentPositionClass">
        {{ content.subTitle }}
      </h3>

      <!-- DIVIDER -->
      <div class="ec-title-divider"></div>



      <!-- DESCRIPTION -->
      <div ref="descWrapper" class="ec-description-wrapper" :class="[
        contentPositionClass,
        { 'ec-description-wrapper--expanded': isExpanded }
      ]">
        <p class="ec-description" v-html="renderedDescription"></p>
      </div>

      <!-- CTA (Only expanded) -->
      <div v-if="content.ctaText && isExpanded" class="ec-cta" :class="ctaAlignmentClass">
        <a :href="content.ctaRedirectLink" target="_blank" class="ec-cta-link" @click="triggerTagging">
          {{ content.ctaText }}
        </a>
      </div>

      <!-- SHOW MORE -->
      <button v-if="content.enableShowMore && isDescriptionLong" class="ec-showmore-btn" :class="showMoreButtonClass"
        @click="toggleExpand">
        {{ isExpanded ? content.showLessButtonText : content.showMoreButtonText }}
      </button>
    </div>

    <div v-else class="desktop-layout">
      <!-- FULL-WIDTH TITLE -->
      <h2 class="ec-title-full" :title="content.title" :class="contentPositionClass">
        {{ truncatedTitle }}
      </h2>

      <div class="ec-title-divider"></div>

      <!-- MAIN CONTENT AREA -->
      <div class="ec-inner">

        <!-- IMAGE -->
        <div v-if="content.cardImageUrl" class="ec-image" :class="imagePositionClass">
          <img :src="content.cardImageUrl" :alt="content.cardImageAlt || ''" />
        </div>

        <!-- TEXT AREA -->
        <div class="ec-text" :class="contentPositionClass">

          <!-- SUBTITLE (inside text area as requested) -->
          <h3 v-if="content.subTitle" class="ec-subtitle">
            {{ content.subTitle }}
          </h3>

          <!-- DESCRIPTION AREA -->
          <div ref="descWrapper" class="ec-description-wrapper"
            :class="{ 'ec-description-wrapper--expanded': isExpanded }">
            <p class="ec-description" v-html="renderedDescription"></p>
          </div>

          <!-- CTA when expanded -->
          <div v-if="content.ctaText && isExpanded" class="ec-cta" :class="ctaAlignmentClass">
            <a :href="content.ctaRedirectLink" target="_blank" class="ec-cta-link" @click="triggerTagging">
              {{ content.ctaText }}
            </a>
          </div>

          <!-- SHOW MORE / LESS -->
          <button v-if="content.enableShowMore && isDescriptionLong" class="ec-showmore-btn"
            :class="showMoreButtonClass" @click="toggleExpand">
            {{ isExpanded ? content.showLessButtonText : content.showMoreButtonText }}
          </button>

        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ExpandableContentCard",

  props: {
    dataExpandableContent: { type: Object, required: true }
  },

  data() {
    return {
      isMobile: false,
      isExpanded: false
    };
  },

  computed: {
    content() {
      return this.dataExpandableContent;
    },

    /* TITLE TRUNCATION (80 chars) */
    truncatedTitle() {
      const title = this.content.title || "";
      return title.length > 80 ? title.slice(0, 80) + "…" : title;
    },

    /* DESCRIPTION LENGTH LOGIC */
    isDescriptionLong() {
      return this.content.description.length > 100;
    },

    DESCRIPTION_LIMIT() {
      if (this.content.enableShowMore) return 100;
      return this.isMobile ? 100 : 250;
    },

    renderedDescription() {
      const text = this.content.description || "";

      if (this.isExpanded) return text;

      return text.length > this.DESCRIPTION_LIMIT
        ? text.slice(0, this.DESCRIPTION_LIMIT) + "…"
        : text;
    },

    /* IMAGE POSITION (desktop) */
    imagePositionClass() {
      const pos = (this.content.imagePosition || "left").toLowerCase();
      return this.isMobile ? "image-top" : `image-${pos}`;
    },

    /* CONTENT (title + desc) POSITION */
    contentPositionClass() {
      const pos = this.isMobile
        ? this.content.mobileContentPosition
        : this.content.deskTopContentPosition;

      return `content-${(pos || "left").toLowerCase()}`;
    },

    /* CTA ALIGN */
    ctaAlignmentClass() {
      const pos = this.content.ctaPosition || "left";
      return `cta-${pos.toLowerCase()}`;
    },

    /* Show More button alignment */
    showMoreButtonClass() {
      const pos = this.content.showMoreButtonPosition || "left";
      return `showmore-${pos.toLowerCase()}`;
    },
  },

  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
    this.checkOverflow();
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobile);
  },

  methods: {
   checkOverflow() {
      const el = this.$refs.descWrapper;
      if (!el) return;

      const computedStyle = window.getComputedStyle(el);
      const maxHeight = parseInt(computedStyle.maxHeight) || el.clientHeight;

      if (this.isExpanded && el.scrollHeight > maxHeight) {
        el.classList.add("has-scroll");
      } else {
        el.classList.remove("has-scroll");
      }
    },
    /* DEVICE DETECTION */
    checkMobile() {
      this.isMobile = window.matchMedia("(max-width: 767px)").matches;
    },

    /* EXPAND / COLLAPSE */
    toggleExpand() {
      this.isExpanded = !this.isExpanded;

      if (this.isExpanded) {
        this.$nextTick(() => {
          if (this.$refs.descWrapper) {
            this.$refs.descWrapper.focus();
          }
          this.checkOverflow();
        });
      }
    },

    /* TAGGING EVENT */
    triggerTagging() {
      if (this.content.tagging) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(this.content.tagging);
      }
    }
  }
};
</script>
